import { computed } from 'vue'
import { useSessionStore } from '@/stores/session'

/**
 * Composable for reading the current project/environment scope from the
 * session store, falling back to null keys when there is no session data.
 *
 * Alongside the project/environment keys, this also resolves their ulids
 * (looked up from the session's project list) so callers can pass them to
 * `usePermissions().can()` for scope-aware permission checks.
 *
 * Usage:
 *   const { current } = useCurrentEnvironment()
 */
export function useCurrentEnvironment() {
    const sessionStore = useSessionStore()

    const current = computed(() => {
        const currentData = sessionStore.data?.current
        if (!currentData) {
            return {
                projectKey: null,
                environmentKey: null,
                projectUlid: null,
                environmentUlid: null,
            }
        }

        const project = sessionStore.data?.projects?.data?.find(
            (x) => x.key === currentData.projectKey
        )
        const environment = project?.environments?.data?.find(
            (x) => x.key === currentData.environmentKey
        )

        return {
            projectKey: currentData.projectKey,
            environmentKey: currentData.environmentKey,
            projectUlid: project?.ulid ?? null,
            environmentUlid: environment?.ulid ?? null,
        }
    })

    return { current }
}
